'use client'

import { useQuery } from '@tanstack/react-query'
import { getAuthHeaders } from '@/lib/api-utils'
import { fetchWithAuthHandler } from '@/lib/api-error-handler'

// Query keys for cache management
export const dashboardKeys = {
    all: ['dashboard'] as const,
    stats: () => [...dashboardKeys.all, 'stats'] as const,
    currentUser: () => [...dashboardKeys.all, 'currentUser'] as const,
    metadata: () => [...dashboardKeys.all, 'metadata'] as const,
    weeklyTime: (from: string, to: string) => [...dashboardKeys.all, 'weeklyTime', from, to] as const,
}

interface DashboardStats {
    assignedIssues: number
    recentActivity: any[]
    upcomingDeadlines: any[]
    timeSpentToday: number
    projectsCount: number
    projects: any[]
    onTrackProjects: number
    overdueTasks: number
    completedThisWeek: number
}

interface Metadata {
    trackers: any[]
    statuses: any[]
    priorities: any[]
    users: any[]
}

// Fetch dashboard stats with timeout
async function fetchDashboardStats(): Promise<DashboardStats> {
    const authHeaders = getAuthHeaders()

    // Add timeout to prevent hanging
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000) // 15 second timeout

    try {
        const response = await fetchWithAuthHandler('/api/dashboard', {
            headers: authHeaders,
            signal: controller.signal
        })

        clearTimeout(timeoutId)

        if (!response.ok) {
            throw new Error(`Failed to fetch dashboard data: ${response.status}`)
        }

        return response.json()
    } catch (error: any) {
        clearTimeout(timeoutId)
        if (error.name === 'AbortError') {
            throw new Error('Dashboard request timed out. Please try again.')
        }
        throw error
    }
}

// Fetch current user with timeout
async function fetchCurrentUser() {
    const authHeaders = getAuthHeaders()

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

    try {
        const userRes = await fetchWithAuthHandler('/api/users/current', {
            headers: authHeaders,
            signal: controller.signal
        })

        clearTimeout(timeoutId)

        if (!userRes.ok) {
            throw new Error('Failed to fetch current user')
        }

        const userData = await userRes.json()
        return userData.user || userData
    } catch (error: any) {
        clearTimeout(timeoutId)
        if (error.name === 'AbortError') {
            throw new Error('User request timed out. Please try again.')
        }
        throw error
    }
}

// Fetch metadata (trackers, statuses, priorities) with timeout
async function fetchMetadata(): Promise<Metadata> {
    const authHeaders = getAuthHeaders()

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

    try {
        const [trackersRes, statusesRes, prioritiesRes] = await Promise.all([
            fetchWithAuthHandler('/api/trackers', { headers: authHeaders, signal: controller.signal }),
            fetchWithAuthHandler('/api/issue_statuses', { headers: authHeaders, signal: controller.signal }),
            fetchWithAuthHandler('/api/enumerations/issue_priorities', { headers: authHeaders, signal: controller.signal })
        ])

        clearTimeout(timeoutId)

        const [trackersData, statusesData, prioritiesData] = await Promise.all([
            trackersRes.ok ? trackersRes.json() : { trackers: [] },
            statusesRes.ok ? statusesRes.json() : { issue_statuses: [] },
            prioritiesRes.ok ? prioritiesRes.json() : { issue_priorities: [] }
        ])

        return {
            trackers: trackersData.trackers || trackersData || [],
            statuses: statusesData.issue_statuses || statusesData || [],
            priorities: prioritiesData.issue_priorities || prioritiesData || [],
            users: []
        }
    } catch (error: any) {
        clearTimeout(timeoutId)
        if (error.name === 'AbortError') {
            throw new Error('Metadata request timed out. Please try again.')
        }
        throw error
    }
}

// Fetch weekly time entries with timeout
async function fetchWeeklyTime(from: string, to: string) {
    const authHeaders = getAuthHeaders()

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

    try {
        const timeResponse = await fetchWithAuthHandler(
            `/api/time?from=${from}&to=${to}`,
            { headers: authHeaders, signal: controller.signal }
        )

        clearTimeout(timeoutId)

        if (!timeResponse.ok) {
            throw new Error('Failed to fetch time entries')
        }

        const timeData = await timeResponse.json()
        return timeData.time_entries || []
    } catch (error: any) {
        clearTimeout(timeoutId)
        if (error.name === 'AbortError') {
            throw new Error('Time entries request timed out. Please try again.')
        }
        throw error
    }
}

// Custom hooks with optimized caching
export function useDashboardStats() {
    return useQuery({
        queryKey: dashboardKeys.stats(),
        queryFn: fetchDashboardStats,
        staleTime: 3 * 60 * 1000, // 3 minutes - increase from 2
        gcTime: 15 * 60 * 1000, // 15 minutes - increase from 10
        refetchOnWindowFocus: false, // Disable - dashboard updates don't need to be instant
        refetchOnMount: false, // Don't refetch if cache is fresh
        retry: 2,
        retryDelay: 1000,
    })
}

export function useCurrentUser() {
    return useQuery({
        queryKey: dashboardKeys.currentUser(),
        queryFn: fetchCurrentUser,
        staleTime: 30 * 60 * 1000, // 30 minutes - increase from 10
        gcTime: 60 * 60 * 1000, // 1 hour
        refetchOnWindowFocus: false,
        refetchOnMount: false, // Don't refetch if cache is fresh
        retry: 2,
        retryDelay: 1000,
    })
}

export function useMetadata() {
    return useQuery({
        queryKey: dashboardKeys.metadata(),
        queryFn: fetchMetadata,
        staleTime: 30 * 60 * 1000, // 30 minutes - increase from 15
        gcTime: 2 * 60 * 60 * 1000, // 2 hours
        refetchOnWindowFocus: false,
        refetchOnMount: false, // Don't refetch if cache is fresh
    })
}

export function useWeeklyTime() {
    // Calculate Monday to Sunday of current week
    const today = new Date()
    const dayOfWeek = today.getDay()
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
    const monday = new Date(today)
    monday.setDate(today.getDate() + mondayOffset)
    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)

    const from = monday.toISOString().split('T')[0]
    const to = sunday.toISOString().split('T')[0]

    return useQuery({
        queryKey: dashboardKeys.weeklyTime(from, to),
        queryFn: () => fetchWeeklyTime(from, to),
        staleTime: 10 * 60 * 1000, // 10 minutes - increase from 5
        gcTime: 30 * 60 * 1000, // 30 minutes - increase from 15
        refetchOnWindowFocus: false, // Disable - time entries don't change that frequently
        refetchOnMount: false, // Don't refetch if cache is fresh
        retry: 2,
        retryDelay: 1000,
    })
}
